import { couponCollectionFragment } from "@saleor/fragments/couponCollection";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  CouponCollectionAssignedCouponsList,
  CouponCollectionAssignedCouponsListVariables
} from "./types/CouponCollectionAssignedCouponsList";
import {
  CouponCollectionDetailQuery,
  CouponCollectionDetailQueryVariables
} from "./types/CouponCollectionDetailQuery";
import {
  CouponCollectionListQuery,
  CouponCollectionListQueryVariables
} from "./types/CouponCollectionListQuery";
import {
  couponFullDetails,
  couponFullDetailsVariables
} from "./types/CouponFullDetails";
import {
  CouponMallsList,
  CouponMallsListVariables
} from "./types/CouponMallsList";
import {
  CouponRedemptionListParent,
  CouponRedemptionListParentVariables
} from "./types/CouponRedemptionListParent";
import {
  CouponRedemptions,
  CouponRedemptionsVariables
} from "./types/CouponRedemptions";
import { Coupons, CouponsVariables } from "./types/Coupons";
import {
  InitialAllMallsFilterDataQuery,
  InitialAllMallsFilterDataQueryVariables
} from "./types/InitialAllMallsFilterDataQuery";
import {
  InitialAssignedMallsFilterDataQuery,
  InitialAssignedMallsFilterDataQueryVariables
} from "./types/InitialAssignedMallsFilterDataQuery";
import { MallTags, MallTagsVariables } from "./types/MallTags";

const couponList = gql`
  ${pageInfoFragment}
  query Coupons(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $sortBy: CouponSortingInput
    $filter: CouponFilterInput
  ) {
    coupons(
      after: $after
      before: $before
      first: $first
      last: $last
      sortBy: $sortBy
      filter: $filter
    ) {
      totalCount
      edges {
        cursor
        node {
          id
          code
          name
          isFeatured
          isNationwide
          hasMalls
          quantity
          dealType
          eventStartDate
          eventEndDate
          category
          redeemType
          usageLimit
          userType
          status
          startDate
          endDate
          occurence
          occurenceExclude
          availableQuantity
          assignee
          malls(first: $first, last: $last) {
            edges {
              node {
                id
                name
              }
            }
          }
          couponRedemptions(first: $first, last: $last) {
            edges {
              node {
                id
                coupon {
                  id
                  code
                  name
                }
                code
                user {
                  id
                  firstName
                  lastName
                }
                claimedAt
                redeemedAt
                status
              }
            }
          }
        }
      }
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

export const useCouponListQuery = makeQuery<Coupons, CouponsVariables>(
  couponList
);

export const couponFullDetailsQuery = gql`
  query couponFullDetails($id: ID!) {
    coupon(id: $id) {
      id
      code
      name
      category
      createdBy {
        promoCreator
        firstName
        lastName
      }
      hasMalls
      isCodeUnlocked
      isFeatured
      isAppExclusive
      isExclusiveRedemption
      isHidden
      isUsageTimerApplied
      isNationwide
      eventStartDate
      eventEndDate
      dealType
      eventLocation
      quantity
      redeemType
      usageLimit
      userType
      assignee
      mainPromoTitle
      mainPromoDescription
      mechanics
      accessPageSubtitle
      accessPageTagline
      accessPageDescription
      currency
      discountValue
      startDate
      endDate
      redeemStartDate
      redeemEndDate
      occurence
      occurenceExclude
      redemptionFrequency
      redemptionExclude
      deeplinkUrl
      shortDynamicLink
      status
      usageTimerHour
      usageTimerMinute
      usageTimerSecond
      collection {
        id
        title
        categories
        endDate
      }
      promoImage {
        alt
        url
      }
      accessImage {
        alt
        url
      }
      malls(first: 100) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;
export const useCouponFullDetailsQuery = makeQuery<
  couponFullDetails,
  couponFullDetailsVariables
>(couponFullDetailsQuery);

export const couponDetailsForSetCodesQuery = gql`
  query couponDetailsForSetCodesQuery($id: ID!) {
    coupon(id: $id) {
      id
      code
      mainPromoTitle
    }
  }
`;

export const useCouponDetailsForSetCodesQuery = makeQuery<
  couponFullDetails,
  couponFullDetailsVariables
>(couponDetailsForSetCodesQuery);

const mallTagsQueries = gql`
  query MallTags(
    $filter: MallInput
    $sortBy: MallSortingInput
    $status: SmopStatusEnum
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    malls(
      first: $first
      after: $after
      last: $last
      before: $before
      filter: $filter
      status: $status
      sortBy: $sortBy
    ) {
      edges {
        cursor
        node {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const useMallQuery = makeQuery<MallTags, MallTagsVariables>(
  mallTagsQueries
);

export const couponRedemptionListParentQuery = gql`
  ${pageInfoFragment}
  query CouponRedemptionListParent(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $filter: CouponRedemptionFilterInput
    $sortBy: CouponRedemptionSortingInput
  ) {
    coupon(id: $id) {
      code
      name
      quantity
      isFeatured
      status
      availableQuantity
      couponRedemptions(
        after: $after
        before: $before
        first: $first
        last: $last
        sortBy: $sortBy
        filter: $filter
      ) {
        edges {
          node {
            id
            code
            status
            coupon {
              id
              name
              code
              availableQuantity
            }
            user {
              id
              firstName
              lastName
              email
            }
            status
            claimedAt
            redeemedAt
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
      }
    }
  }
`;
export const useCouponRedemptionListParentQuery = makeQuery<
  CouponRedemptionListParent,
  CouponRedemptionListParentVariables
>(couponRedemptionListParentQuery);

export const couponRedemptionList = gql`
  ${pageInfoFragment}
  query CouponRedemptions(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $filter: CouponRedemptionFilterInput
    $sortBy: CouponRedemptionSortingInput
  ) {
    couponRedemptions(
      after: $after
      before: $before
      first: $first
      last: $last
      sortBy: $sortBy
      filter: $filter
    ) {
      edges {
        cursor
        node {
          id
          code
          user {
            id
            firstName
            lastName
            email
          }
          status
          coupon {
            id
            name
            code
          }
          claimedAt
          redeemedAt
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const useCouponRedemptionListQuery = makeQuery<
  CouponRedemptions,
  CouponRedemptionsVariables
>(couponRedemptionList);

export const couponMallsListQuery = gql`
  query CouponMallsList(
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    coupon(id: $id) {
      id
      code
      name
      malls(after: $after, before: $before, first: $first, last: $last) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

export const useCouponMallsListQuery = makeQuery<
  CouponMallsList,
  CouponMallsListVariables
>(couponMallsListQuery);

export const initialAssignedMallsFilterDataQuery = gql`
  query InitialAssignedMallsFilterDataQuery($mall: [ID!]) {
    me {
      malls(first: 5, filter: { id: $mall, status: "ACT" }) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

export const useInitialAssignedMallsFilterDataQuery = makeQuery<
  InitialAssignedMallsFilterDataQuery,
  InitialAssignedMallsFilterDataQueryVariables
>(initialAssignedMallsFilterDataQuery);

export const initialAllMallsFilterDataQuery = gql`
  query InitialAllMallsFilterDataQuery($mall: [ID!]) {
    malls(first: 5, filter: { id: $mall, status: "ACT" }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const useInitialAllMallsFilterDataQuery = makeQuery<
  InitialAllMallsFilterDataQuery,
  InitialAllMallsFilterDataQueryVariables
>(initialAllMallsFilterDataQuery);

export const couponCollectionDetailQuery = gql`
  ${couponCollectionFragment}
  query CouponCollectionDetailQuery($id: ID!) {
    couponCollection(instanceId: $id) {
      ...CouponCollectionFragment
    }
  }
`;
export const useCouponCollectionDetailQuery = makeQuery<
  CouponCollectionDetailQuery,
  CouponCollectionDetailQueryVariables
>(couponCollectionDetailQuery);

export const couponCollectionListQuery = gql`
  ${pageInfoFragment}
  query CouponCollectionListQuery(
    $filter: CouponCollectionFilterInput
    $sortBy: CouponCollectionSortingInput
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    couponCollections(
      filter: $filter
      sortBy: $sortBy
      before: $before
      after: $after
      first: $first
      last: $last
    ) {
      edges {
        node {
          id
          title
          categories
          __typename
          status
          writeup
          startDate
          endDate
          isFeatured
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const useCouponCollectionListQuery = makeQuery<
  CouponCollectionListQuery,
  CouponCollectionListQueryVariables
>(couponCollectionListQuery);

export const couponCollectionAssignedCouponList = gql`
  ${pageInfoFragment}
  query CouponCollectionAssignedCouponsList(
    $id: ID!
    $before: String
    $after: String
    $first: Int
    $last: Int
    $sortBy: CouponSortingInput
    $filter: CouponFilterInput
  ) {
    couponCollection(instanceId: $id) {
      id
      title
      coupons(
        before: $before
        after: $after
        first: $first
        last: $last
        sortBy: $sortBy
        filter: $filter
      ) {
        pageInfo {
          ...PageInfoFragment
        }
        edges {
          node {
            id
            name
            dealType
            status
          }
        }
      }
    }
  }
`;
export const useCouponCollectionAssignedCouponList = makeQuery<
  CouponCollectionAssignedCouponsList,
  CouponCollectionAssignedCouponsListVariables
>(couponCollectionAssignedCouponList);

export const couponUnlockCodeQuery = gql`
  query couponUnlockCode($unlockCodeId: ID!) {
    couponUnlockCode(id: $unlockCodeId) {
      code
      description
      tags
      usageLimit
      coupon {
        code
        mainPromoTitle
      }
      qrCode
    }
  }
`;

export const couponUnlockCodesStringQuery = gql`
  query couponUnlockCodesString($couponId: ID!) {
    couponUnlockCodes(
      first: 100
      filter: { coupon: $couponId }
      sortBy: { direction: ASC, behavior: CASE_INSENSITIVE, field: CODE }
    ) {
      totalCount
      edges {
        node {
          code
        }
      }
    }
  }
`;

export const couponUnlockCodesQuery = gql`
  query couponUnlockCodes(
    $couponId: ID!
    $isCodeListOnly: Boolean = false
    $before: String
    $after: String
    $first: Int
    $last: Int
    $sortBy: CouponUnlockCodeSortingInput!
  ) {
    couponUnlockCodes(
      first: $first
      last: $last
      after: $after
      before: $before
      filter: { coupon: $couponId }
      sortBy: $sortBy
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id @skip(if: $isCodeListOnly)
          code
          usageLimit @skip(if: $isCodeListOnly)
          description @skip(if: $isCodeListOnly)
        }
      }
      totalCount
    }
  }
`;

export const couponUnlockCodeTemplate = gql`
  query CouponUnlockCodeTemplate {
    couponUnlockCodeTemplate
  }
`;
